import { DocumentNode } from 'graphql/language/ast'
import request from 'graphql-request'
import gql from 'graphql-tag'
import { useActiveWeb3React } from 'hooks/web3'
import { useCachedSWR } from 'lib/hooks/useCachedSWR'
import { useCallback, useEffect, useState } from 'react'

const BLACKLISTED_TOKENS = ['0x979b05e391c3544824e7ec19d064ae4f0198741c']

const useSWR = <T>(key: string, fetcher: (key: string) => Promise<T>) => {
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey((prev) => prev + 1)
    }, 30000) // 30 seconds

    return () => clearInterval(interval)
  }, [])

  return useCachedSWR<T>(`${key}-${refreshKey}`, fetcher, 30000)
}

const DEFAULT_URL = 'https://xswap.squids.live/xswap:main/api/graphql'

const useApiQuery = (query: DocumentNode, variables: any = undefined) => {
  return useCallback(() => request(`${DEFAULT_URL}`, query, variables), [query, variables])
}

export interface IApiPool {
  id: string
  token1: string
  token0: string
  APR: number
  TVL: number
  reserve0: string
  reserve1: string
  volume_24h: number
  volume_7d: number
}

const POOLS_QUERY = gql`
  query MyQuery {
    get_pairs_stats {
      APR
      TVL
      id
      reserve0
      reserve1
      token0
      token1
      volume_24h
      volume_7d
    }
  }
`

const USER_POOLS_QUERY = gql`
  query userPools($address: String!) {
    get_user_info(address: $address) {
      pair_address
    }
  }
`

export interface IUserPool {
  pair_address: string
}

export const useUserPools = (): {
  list: IUserPool[]
  loading: boolean
  error: any
} => {
  const { account } = useActiveWeb3React()
  const fetchApi = useApiQuery(USER_POOLS_QUERY, { address: account })
  const { data, error } = useSWR(`useUserPools`, fetchApi)

  return {
    list: data ? data.get_user_info : [],
    loading: !data,
    error,
  }
}

export function useLiquidityPools(): {
  list: IApiPool[]
  mapped: Record<string, IApiPool>
  loading: boolean
  error: any
} {
  const fetchApi = useApiQuery(POOLS_QUERY)
  const { data, error } = useSWR(`useLiquidityPools`, fetchApi)

  const filteredData = data?.get_pairs_stats.filter(
    (pool: IApiPool) => !BLACKLISTED_TOKENS.includes(pool.token0) && !BLACKLISTED_TOKENS.includes(pool.token1)
  )

  return {
    list: filteredData ? filteredData : [],
    mapped: filteredData
      ? data.get_pairs_stats.reduce((acc: Record<string, IApiPool>, pool: IApiPool) => {
          acc[pool.id] = pool
          return acc
        }, {})
      : {},
    loading: !data,
    error,
  }
}
